/* eslint require-jsdoc: 'error', valid-jsdoc: ["error", { "requireReturn": false }] */

import ColumnDataset from '@fusioncharts/charts/src/dataset/column';
import { hasSVG, getMouseCoordinate, pluck, pluckNumber, getValidValue, parseUnsafeString,
  mathAbs, preDefStr, parseTooltext, snapPoint } from '@fusioncharts/core/src/lib';
import dragColumnAnimation from './index.animation';
import { addDep } from '@fusioncharts/core/src/dependency-manager';

let SETROLLOVERATTR = preDefStr.setRolloverAttrStr,
  SETROLLOUTATTR = preDefStr.setRolloutAttrStr,
  ROLLOUT = 'DataPlotRollOut',
  ROLLOVER = 'DataPlotRollOver',
  MOUSEOVER = 'mouseOver',
  MOUSEOUT = 'mouseOut',
  DEFAULT = 'default',
  DATAPLOTCLICK = preDefStr.DATAPLOTCLICK,
  dragMouseAttr = (hasSVG && 'ns-resize') || 'n-resize',
  _getJSONData,
  _restore,
  UNDEF,
  updateDataValue = function (event, chart) {
    let element = this,
      yAxis = chart.getChildren('yAxis')[0],
      container = chart.getFromEnv('chart-container'),
      eventArgs = element.data('eventArgs'),
      mouseCoordinate;

    mouseCoordinate = getMouseCoordinate(container, event, chart);
    eventArgs.value = yAxis.getValue(mouseCoordinate.chartY);

    element.data('eventArgs', eventArgs);
  };
addDep({
  name: 'dragColumnAnimation',
  type: 'animationRule',
  extension: dragColumnAnimation
});

/**
 * dataset class of dragcolumn inheriting from column class
 */
class DragColumnDataset extends ColumnDataset {
  /**
   * Sets the type of the component
   * @return {string} type
   */
  getType () {
    return 'dataset';
  }
  /**
   * Sets the name of the component
   * @return {string} name
   */
  getName () {
    return 'dragColumn';
  }
  /**
    * Function for parsing all the attributes and value given by the user at chart,dataset and set level.
    * This function is called once from the init() function of the Column class.
    * @param  {Object} datasetJSON JSON for dataset configurations
    */
  configureAttributes (datasetJSON) {
    var dataset = this,
      conf = dataset.config,
      chartAttr = dataset.getFromEnv('chart-attrib'),
      JSONData = conf.JSONData;

    conf.allowDrag = pluckNumber(JSONData.allowdrag, 1);
    conf.allowNegDrag = pluckNumber(JSONData.allownegativedrag, 1);
    conf.allowAxisChange = pluckNumber(chartAttr.allowaxischange, 1);
    conf.snapToDivOnly = pluckNumber(chartAttr.snaptodivonly, 0);
    conf.snapToDiv = conf.snapToDivOnly ? 1 : pluckNumber(chartAttr.snaptodiv, 1);
    conf.doNotSnap = pluckNumber(chartAttr.donotsnap, 0);
    conf.snapToDivRelaxation = pluckNumber(chartAttr.snaptodivrelaxation, 10);
    if (conf.doNotSnap) {
      conf.snapToDiv = conf.snapToDivOnly = 0;
    }
    super.configureAttributes(datasetJSON);
  }
  /**
   * method to configure a particular data plot object
   * @param {number} i index of the data object to be configured
   * @param {Object} setData particular plot information in of i'th index in input json
   */
  _plotConfigure (i, setData) {
    let conf = this.config,
      dataStore = this.components.data,
      dataObj,
      config;
    super._plotConfigure(i, setData);
    dataObj = dataStore[i];
    config = dataObj.config;
    config.allowDrag = pluckNumber(setData.allowdrag, conf.allowDrag);
    config.allowNegDrag = pluckNumber(setData.allownegativedrag, conf.allowNegDrag);
  }

  /**
  * Function called when drag is triggered over the datasets
  * @param {String} action dragstart/dragmove/dragend action
  * @param {*} oriEvent The original mouse event
  * @param {*} index the index of the data over which drag is triggered
  * @param {*} coordinateData the mouse-coordinate wrt to the chart container
  */
  drag (action, oriEvent, index, coordinateData) {
    var dataset = this,
      coordinate = coordinateData,
      datasetConf = dataset.config,
      chart = dataset.getFromEnv('chart'),
      chartConfig = chart.config,
      canvasTop = chartConfig.canvasTop,
      canvasBottom = chartConfig.canvasBottom,
      setElement,
      config,
      toolTipController = dataset.getFromEnv('toolTipController'),
      NumberFormatter = dataset.getFromEnv('number-formatter'),
      dataStore = dataset.components.data,
      yActual,
      value,
      formattedVal,
      allowNegDrag,
      yPos,
      angle,
      height,
      rolloverdata,
      lowerDragBoundary,
      chartY,
      fill,
      rolloutedata,
      canvas = dataset.getFromEnv('paper').canvas,
      style = canvas.style,
      yAxis = dataset.getFromEnv('yAxis'),
      yBasePos = yAxis.yBasePos,
      plotIndex,
      eventArgs,
      eventArgsArr,
      yaxisName = chartConfig.yaxisname,
      xaxisName = chartConfig.xaxisname,
      parserConfig = {
        xaxisName,
        yaxisName
      },
      ticks = yAxis.getTicks(),
      data,
      tolerance = chartConfig.dragTolerance + 1,
      setTooltext,
      toolText,
      allowDrag,
      pointerMouseAttr = DEFAULT,
      macroIndices = [1, 2, 3, 4, 5, 6, 7],
      JSONData = datasetConf.JSONData,
      chartAttr = dataset.getFromEnv('chart-attrib'),
      useplotgradientcolor = chartConfig.useplotgradientcolor,
      useroundedges = chartConfig.useroundedges,
      chartContainer = dataset.getFromEnv('chart-container'),
      snapToDivRelaxation = mathAbs(yAxis.getValue(yAxis.getPixel(0) - datasetConf.snapToDivRelaxation)),
      snapPixel = datasetConf.snapToDivOnly
        ? (mathAbs(ticks[1] - ticks[0]) * 0.5) : snapToDivRelaxation;

    coordinate = coordinate || getMouseCoordinate(chartContainer, oriEvent, chart);

    switch (action) {
      case 'dragstart':
        data = dataStore[index];
        config = data.config;
        setElement = data.graphics.element;
        yPos = data._yPos;
        height = data._height;
        yActual = (config.setValue < 0) ? (yPos + height) : yPos;
        allowDrag = config.allowDrag;
        chartY = coordinate.chartY;
        if (allowDrag && chartY >= yActual - tolerance &&
          chartY <= yActual + tolerance) {
          // set drag flag
          config.dragStart = true;
          config._pointerDy = 0;
          config._dragStartY = chartY;
          // difference on actualY and chartY
          config._dragBuffer = yActual - chartY;

          data.startValue = config.setValue;
          data.name = datasetConf.seriesname;
          data.datasetIndex = datasetConf.index;
          data.dragged = true;
        } else {
          config.dragStart = false;
        }
        break;

      case 'dragmove':
        data = dataStore[index];
        config = data.config;
        if (config.dragStart) {
          setElement = data.graphics.element;
          plotIndex = config.plotIndex;
          allowNegDrag = config.allowNegDrag;
          lowerDragBoundary = allowNegDrag ? canvasBottom : yBasePos;
          chartY = coordinate.chartY;
          config._rollOverResponseSetterFire = false;
          style.cursor = dragMouseAttr;
          // counter on drag move
          config._pointerDy++;
          // calculate chartY with _dragBuffer
          chartY += config._dragBuffer;
          // update chartY on canvas edge crossing
          if (chartY < canvasTop) {
            chartY = canvasTop;
          } else if (chartY > lowerDragBoundary) {
            chartY = lowerDragBoundary;
          }
          // calculate yPos
          yPos = yBasePos < chartY ? yBasePos : chartY;
          // calculate height
          height = mathAbs(yBasePos - chartY);
          data._yPos = yPos;
          data._height = height;
          yActual = (yPos >= yBasePos) ? (yPos + height) : yPos;
          config._y = value = config.setValue = yAxis.getValue(yActual);
          formattedVal = NumberFormatter.dataLabels(value);
          config.toolTipValue = formattedVal;
          config.displayValue = pluck(config.setDisplayValue, formattedVal);
          if (useplotgradientcolor && !useroundedges) {
            config.colorArr[0].FCcolor.angle = angle = yPos < yBasePos ? 90 : 270;
          }

          setElement.attr({
            y: data._yPos,
            height: data._height
          });

          dataset.parseLabelAttributes(data);
          dataset.drawLabel(plotIndex, plotIndex + 1);
          data.graphics.element = setElement;
          toolTipController.hide(datasetConf.currentToolTip);

          // fire dataplotDragStart event
          if (config._pointerDy === 1) {
            eventArgs = {
              index: index,
              dataIndex: index,
              datasetIndex: data.datasetIndex,
              startValue: data.startValue,
              datasetName: data.name,
              color: config.originalPlotColor,
              gradientColor: datasetConf.plotgradientcolor,
              alpha: config.plotFillAlpha,
              borderColor: datasetConf.plotbordercolor,
              borderAlpha: config.plotBorderAlpha,
              borderThickness: chartConfig.plotborderthickness,
              borderDashed: config.dashed,
              hoverColor: config.hoverColor,
              hoverAlpha: config.hoverAlpha,
              borderHoverColor: config.hoverBorderColor,
              borderHoverAlpha: config.hoverBorderAlpha
            };
            chart.fireChartInstanceEvent('dataplotDragStart', eventArgs);
          }
        }
        break;
      case 'dragend':
        let plotHeight;
        data = dataStore[index];
        config = data.config;
        plotHeight = data._height;
        if (config.dragStart) {
          yPos = data._yPos;
          setElement = data.graphics.element;
          setTooltext = getValidValue(parseUnsafeString(pluck(data.tooltext,
            JSONData.plottooltext, chartAttr.plottooltext), false));
          chartY = coordinate.chartY;
          dataset.setMaxMin(data);
          // chart._setDataLimits();
          if (config.givenPlotShowValue === 0) {
            config.showValue = 0;
          } else if (config.givenPlotShowValue === 1) {
            config.showValue = 1;
          } else if (config.givenPlotShowValue !== 1 && datasetConf.showValues) {
            if (Math.sqrt(plotHeight * plotHeight) < config.minplotheightforvalue) {
              config.showValue = 0;
            } else {
              config.showValue = 1;
            }
          }
          if (datasetConf.snapToDiv || datasetConf.snapToDivOnly) {
            config.setValue = snapPoint({ snapPixel, datasetConf }, ticks, data);
            chartY = yAxis.getPixel(config.setValue);
            yPos = yBasePos < chartY ? yBasePos : chartY;
            // calculate height
            height = mathAbs(yBasePos - chartY);
            data._yPos = yPos;
            data._height = height;
            setElement.attr({
              y: yPos,
              height: height
            });
            formattedVal = NumberFormatter.dataLabels(config.setValue);
            config.toolTipValue = formattedVal;
            config.displayValue = pluck(config.setDisplayValue, formattedVal);
            dataset.parseLabelAttributes(data);
            dataset.drawLabel(plotIndex, plotIndex + 1);
          }

          eventArgs = {
            index: index,
            dataIndex: index,
            datasetIndex: data.datasetIndex,
            startValue: data.startValue,
            endValue: config.setValue,
            datasetName: data.name,
            color: config.originalPlotColor,
            gradientColor: datasetConf.plotgradientcolor,
            alpha: config.plotFillAlpha,
            borderColor: datasetConf.plotbordercolor,
            borderAlpha: config.plotBorderAlpha,
            borderThickness: chartConfig.plotborderthickness,
            borderDashed: config.dashed,
            hoverColor: config.hoverColor,
            hoverAlpha: config.hoverAlpha,
            borderHoverColor: config.hoverBorderColor,
            borderHoverAlpha: config.hoverBorderAlpha,
            id: config.id
          };
          eventArgsArr = [
            chart.getFromEnv('chartInstance').id,
            eventArgs.dataIndex,
            eventArgs.datasetIndex,
            eventArgs.datasetName,
            eventArgs.startValue,
            eventArgs.endValue
          ];

          // Update dataValue
          updateDataValue.call(setElement, oriEvent, chart);
          if (config._pointerDy) {
            chart.fireChartInstanceEvent('dataplotDragEnd', eventArgs);

            // Fire the ChartUpdated event
            chart.fireChartInstanceEvent('chartupdated', eventArgs, eventArgsArr);
          }
          if (useplotgradientcolor && !useroundedges) {
            angle = yPos >= yBasePos ? 90 : 270;
            if ((rolloverdata = setElement.data(SETROLLOVERATTR)) && rolloverdata.fill) {
              fill = rolloverdata.fill;
              fill = fill.split('-');
              fill[0] = angle;
              rolloverdata.fill = fill.join('-');
            }
            if ((rolloutedata = setElement.data(SETROLLOUTATTR)) && rolloutedata.fill) {
              fill = rolloutedata.fill;
              fill = fill.split('-');
              fill[0] = angle;
              rolloutedata.fill = fill.join('-');
            }
          }
          // difference on actualY and chartY
          config._dragBuffer = 0;
          // counter on drag move
          config._pointerDy = 0;
          if (setTooltext !== UNDEF) {
            parserConfig.formattedValue = config.toolTipValue;
            parserConfig.label = config.label;
            toolText = parseTooltext(setTooltext, macroIndices,
              parserConfig, { value: config.toolTipValue }, UNDEF, JSONData);
            config.setTooltext = toolText;
            setTooltext = toolText;
            config.toolText = toolText;
          }
          toolText = config.finalTooltext = config.toolText !== false ? (config.toolText +
            (setTooltext ? '' : config.toolTipValue)) : '';
          if (!(chartY >= yActual - tolerance &&
                  chartY <= yActual + tolerance)) {
            style.cursor = pointerMouseAttr;
          }
          config.dragStart = false;
        }
    }
  }

  /**
   * This method handles all mouse events of an dataset.
   * @param {string} eventType name of the event
   * @param {number} plotIndex index of the plot where this event has been occured
   * @param {Event} e reference of the original mouse event
   */
  _firePlotEvent (eventType, plotIndex, e) {
    var dataset = this,
      chart = dataset.getFromEnv('chart'),
      datasetConf = dataset.config,
      currentToolTip = datasetConf.currentToolTip,
      chartConfig = dataset.getFromEnv('chartConfig'),
      drawTrendRegion = chartConfig.drawTrendRegion,
      // chartComp = chart.components,
      paper = dataset.getFromEnv('paper'),
      canvas = paper.canvas,
      style = canvas.style,
      toolTipController = dataset.getFromEnv('toolTipController'),
      data = dataset.components.data[plotIndex],
      config = data.config,
      setElement = data.graphics.element,
      toolText = config.finalTooltext || config.toolText,
      // tip = lib.toolTip,
      originalEvent = e.originalEvent,
      pointerMouseAttr = DEFAULT,
      tolerance,
      coordinate = getMouseCoordinate(dataset.getFromEnv('chart-container'), originalEvent, chart),
      chartY = coordinate.chartY,
      yPos = data._yPos,
      height = data._height,
      yAxis = dataset.getFromEnv('yAxis'),
      yBasePos = yAxis.yBasePos,
      allowDrag = config.allowDrag,
      yActual,
      colTop = yPos,
      colBottom = yPos + height;

    tolerance = chartConfig.dragTolerance + 1;

    yActual = (yPos >= yBasePos) ? (yPos + height) : yPos;
    if (setElement) {
      switch (eventType) {
        case 'fc-mouseover' :
          if (chartY <= colBottom - tolerance && chartY >= colTop + tolerance && !drawTrendRegion && toolText) {
            if (currentToolTip) {
              toolTipController.draw(originalEvent, toolText, currentToolTip);
            } else {
              currentToolTip = datasetConf.currentToolTip = toolTipController.draw(originalEvent, toolText);
            }
          }

          // fire _rollOverResponseSetter if its not already fired and
          // cursor position is on column body
          if (!config._rollOverResponseSetterFire && chartY <= colBottom && chartY >= colTop) {
            DragColumnDataset._rolloverResponseSetter(chart, setElement, e, this);
            // set flag true as _rollOverResponseSetter is fired
            config._rollOverResponseSetterFire = true;
          }
          break;

        case 'fc-mouseout' :
          style.cursor = pointerMouseAttr;
          // fire _rolloutResponseSetter if _rolloverResponseSetter is fired
          config._rollOverResponseSetterFire &&
          DragColumnDataset._rolloutResponseSetter(chart, setElement, e, this);
          // set flag false as _rolloutResponseSetter is fired
          config._rollOverResponseSetterFire = false;
          toolTipController.hide(datasetConf.currentToolTip);
          break;

        case 'fc-click' :
          chart.plotEventHandler(setElement, e, DATAPLOTCLICK);
          break;

        case 'fc-mousemove' :
          if (!config.dragStart) {
            yActual = (config.setValue < 0) ? (yPos + height) : yPos;
            if (allowDrag && chartY >= yActual - tolerance && chartY <= yActual + tolerance) {
              style.cursor = dragMouseAttr;
              toolTipController.hide(datasetConf.currentToolTip);
            } else {
              style.cursor = pointerMouseAttr;
              if (config._rollOverResponseSetterFire && !drawTrendRegion && toolText) {
                if (currentToolTip) {
                  toolTipController.draw(originalEvent, toolText, currentToolTip);
                } else {
                  currentToolTip = datasetConf.currentToolTip = toolTipController.draw(originalEvent, toolText);
                }
              }
            }
            // fire _rollOverResponseSetter if its not already fired and
            // cursor position is on column body
            if (!config._rollOverResponseSetterFire && chartY <= colBottom && chartY >= colTop) {
              DragColumnDataset._rolloverResponseSetter(chart, setElement, e);
              config._rollOverResponseSetterFire = true;
            } else if (config._rollOverResponseSetterFire &&
              !(chartY <= colBottom && chartY >= colTop)) {
              toolTipController.hide(datasetConf.currentToolTip);
              config._rollOverResponseSetterFire = false;
              DragColumnDataset._rolloutResponseSetter(chart, setElement, e, this);
            }
          }
          break;
      }
    }
  }

  /**
   * Function called as a callback when mouse pointer hovers over a graphic element
   * @param {Object} chart The chart object
   * @param {Object} elem The graphic element for which the function is called
   * @param {Object} event The event against which the function is called.
   * @param {Component} dataset - dataset component
   */
  static _rolloverResponseSetter (chart, elem, event, dataset) {
    let elData = elem && elem.getData(),
      animationManager = chart.getFromEnv('animationManager');
    // Check whether the plot is in dragged state or not if
    // drag then dont fire rolloverevent
    if (elem && elData.showHoverEffect !== 0 && elData.draged !== true && elem) {
      animationManager.setAnimationState(MOUSEOVER);
      animationManager.setAnimation({
        el: elem,
        label: 'rect',
        component: dataset,
        attr: elem.getData().setRolloverAttr
      });
      chart.plotEventHandler(elem, event, ROLLOVER);
    }
  }

  /**
   * Function called as a callback when mouse pointer hovers out a graphic element
   * @param {Object} chart The chart object
   * @param {Object} elem The graphic element for which the function is called
   * @param {Object} event The event against which the function is called.
   * @param {Component} dataset - dataset component
   */
  static _rolloutResponseSetter (chart, elem, event, dataset) {
    let elData = elem && elem.getData(),
      animationManager = chart.getFromEnv('animationManager');
    // Check whether the plot is in draggedstate or not if drag then dont fire rolloutevent
    if (elem && elData.showHoverEffect !== 0 && elData.draged !== true) {
      animationManager.setAnimationState(MOUSEOUT);
      animationManager.setAnimation({
        el: elem,
        label: 'rect',
        component: dataset,
        attr: elem.getData().setRolloutAttr
      });
      chart.plotEventHandler(elem, event, ROLLOUT);
    }
  }

  /**
   * Fucntion to get the updated values of data when they are dragged.
   * @return {Object} The corresponding data Array of the dataset containing the values of all the sets.
   */
  getJSONData () {
    var dataset = this,
      JSONData = dataset.config.JSONData.data,
      dataStore = dataset.components.data,
      dataArr = [],
      obj = {},
      updatedDataObj,
      dataObj,
      prop,
      len,
      i;

    for (i = 0, len = JSONData.length; i < len; i++) {
      dataObj = JSONData[i];
      updatedDataObj = dataStore[i];
      obj = {};
      for (prop in dataObj) {
        if (prop === 'value') {
          obj[prop] = updatedDataObj.config.setValue;
        } else {
          obj[prop] = dataObj[prop];
        }
      }
      dataArr.push(obj);
    }
    return {
      data: dataArr
    };
  }

  /**
   * Function to restore the values of the dataset to its original
   */
  restore () {
    var dataset = this;
    dataset.setData(dataset.config.JSONData, true);
  }
}
_getJSONData = DragColumnDataset.prototype.getJSONData;
_restore = DragColumnDataset.prototype.restore;
export default DragColumnDataset;
export { _getJSONData, _restore, updateDataValue };
